import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type {
  FeatureEntitlement,
  SupplierEntitlements,
  FallbackMode,
  EntitlementReason,
  EntitlementStatus,
} from "@/lib/featureCatalog";

interface SubscriptionFeatureRow {
  id: string;
  key: string;
  label: string;
  category: string;
  fallback_mode: string;
  required_service: string | null;
  required_plugin: string | null;
  is_active: boolean;
}

interface SupplierProfile {
  enabled_services: string[];
  enabled_plugins: string[];
  premium_modules: string[];
}

// Global cache for feature catalog (same for all suppliers)
let featureCatalogCache: SubscriptionFeatureRow[] | null = null;
let planFeaturesCache: Record<string, Set<string>> | null = null;

export function useSupplierEntitlements(
  supplierId: string | null | undefined,
  supplierProfile?: {
    enabled_services?: string[];
    enabled_plugins?: string[];
    premium_modules?: string[];
  }
): SupplierEntitlements {
  const [isLoading, setIsLoading] = useState(true);
  const [planKey, setPlanKey] = useState<string | null>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const [features, setFeatures] = useState<Record<string, FeatureEntitlement>>({});
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!supplierId) {
      setIsLoading(false);
      return;
    }

    resolvedRef.current = false;
    let cancelled = false;

    async function resolve() {
      try {
        // 1. Fetch feature catalog (cached globally)
        if (!featureCatalogCache) {
          const { data: featData } = await supabase
            .from("subscription_features")
            .select("id, key, label, category, fallback_mode, required_service, required_plugin, is_active")
            .eq("is_active", true)
            .order("sort_order");
          featureCatalogCache = (featData as SubscriptionFeatureRow[]) || [];
        }

        // 2. Fetch plan→feature mappings (cached globally)
        if (!planFeaturesCache) {
          const { data: pfData } = await supabase
            .from("plan_features")
            .select("plan_id, feature_id");

          const { data: plansData } = await supabase
            .from("subscription_plans")
            .select("id, key")
            .eq("is_active", true);

          const planIdToKey: Record<string, string> = {};
          (plansData || []).forEach((p: { id: string; key: string }) => {
            planIdToKey[p.id] = p.key;
          });

          const featureIdToKey: Record<string, string> = {};
          (featureCatalogCache || []).forEach((f) => {
            featureIdToKey[f.id] = f.key;
          });

          planFeaturesCache = {};
          (pfData || []).forEach((pf: { plan_id: string; feature_id: string }) => {
            const pk = planIdToKey[pf.plan_id];
            if (pk) {
              if (!planFeaturesCache![pk]) planFeaturesCache![pk] = new Set();
              const fk = featureIdToKey[pf.feature_id];
              if (fk) planFeaturesCache![pk].add(fk);
            }
          });
        }

        if (cancelled) return;

        // 3. Fetch supplier subscription
        const { data: subData } = await supabase
          .from("supplier_subscriptions")
          .select("plan_id, is_active, expires_at")
          .eq("supplier_id", supplierId)
          .eq("is_active", true)
          .maybeSingle();

        let activePlanKey: string | null = null;
        let activePlanName: string | null = null;

        if (subData) {
          const now = new Date();
          const notExpired = !subData.expires_at || new Date(subData.expires_at) > now;
          if (notExpired) {
            const { data: planRow } = await supabase
              .from("subscription_plans")
              .select("key, name")
              .eq("id", subData.plan_id)
              .single();
            if (planRow) {
              activePlanKey = planRow.key;
              activePlanName = planRow.name;
            }
          }
        }

        if (cancelled) return;

        // 4. Fetch overrides
        const { data: overrides } = await supabase
          .from("supplier_feature_overrides")
          .select("feature_key, is_enabled")
          .eq("supplier_id", supplierId);

        const overrideMap: Record<string, boolean> = {};
        (overrides || []).forEach((o: { feature_key: string; is_enabled: boolean }) => {
          overrideMap[o.feature_key] = o.is_enabled;
        });

        if (cancelled) return;

        // 5. Get supplier profile for service/plugin checks
        let profile: SupplierProfile = {
          enabled_services: supplierProfile?.enabled_services || [],
          enabled_plugins: supplierProfile?.enabled_plugins || [],
          premium_modules: supplierProfile?.premium_modules || [],
        };

        if (!supplierProfile) {
          const { data: sp } = await supabase
            .from("suppliers")
            .select("enabled_services, enabled_plugins, premium_modules")
            .eq("id", supplierId)
            .single();
          if (sp) {
            profile = {
              enabled_services: (sp as SupplierProfile).enabled_services || [],
              enabled_plugins: (sp as SupplierProfile).enabled_plugins || [],
              premium_modules: (sp as SupplierProfile).premium_modules || [],
            };
          }
        }

        if (cancelled) return;

        // 6. Bridge: if no subscription, check legacy premium_modules
        let useBridge = false;
        if (!activePlanKey && profile.premium_modules.length > 0) {
          activePlanKey = "premium";
          activePlanName = "Premium (legacy)";
          useBridge = true;
        }

        // 7. Resolve entitlements
        const planFeatureSet = activePlanKey
          ? planFeaturesCache?.[activePlanKey] || new Set<string>()
          : new Set<string>();

        const resolved: Record<string, FeatureEntitlement> = {};

        for (const feat of featureCatalogCache || []) {
          const fallbackMode = feat.fallback_mode as FallbackMode;
          let status: EntitlementStatus = fallbackMode;
          let reason: EntitlementReason = "no_plan";

          // Override check (highest priority)
          if (feat.key in overrideMap) {
            if (overrideMap[feat.key]) {
              status = "allowed";
              reason = "override_grant";
            } else {
              status = fallbackMode;
              reason = "override_deny";
            }
          } else if (planFeatureSet.has(feat.key)) {
            status = "allowed";
            reason = useBridge ? "bridge" : "plan";
          }

          // Service/plugin dependency checks (even for overrides)
          if (status === "allowed") {
            if (feat.required_service && !profile.enabled_services.includes(feat.required_service)) {
              status = fallbackMode;
              reason = "missing_service";
            } else if (feat.required_plugin && !profile.enabled_plugins.includes(feat.required_plugin)) {
              status = fallbackMode;
              reason = "missing_plugin";
            }
          }

          resolved[feat.key] = {
            key: feat.key,
            label: feat.label,
            status,
            fallbackMode,
            reason,
          };
        }

        if (cancelled) return;

        setPlanKey(activePlanKey);
        setPlanName(activePlanName);
        setFeatures(resolved);
        resolvedRef.current = true;
      } catch (err) {
        console.error("Failed to resolve supplier entitlements:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [supplierId, supplierProfile?.enabled_services?.join(","), supplierProfile?.enabled_plugins?.join(","), supplierProfile?.premium_modules?.join(",")]);

  const canUse = useCallback(
    (featureKey: string): boolean => {
      if (isLoading) return false;
      return features[featureKey]?.status === "allowed";
    },
    [features, isLoading]
  );

  const getFeature = useCallback(
    (featureKey: string): FeatureEntitlement | null => {
      return features[featureKey] || null;
    },
    [features]
  );

  return {
    planKey,
    planName,
    features,
    isLoading,
    canUse,
    getFeature,
  };
}

// Invalidate caches (call when admin updates features/plans)
export function invalidateEntitlementCaches() {
  featureCatalogCache = null;
  planFeaturesCache = null;
}
